import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { useTranslation } from 'next-i18next';
import { ReactNode, useCallback, useMemo } from 'react';
import { ButtonV2, Snackbar } from '@bridebook/ui';
import { TSnackbarMode } from '@bridebook/ui/src/components/bbcommon/snackbar/snackbar-content';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import { setAIPlannerDisabled } from '@bridebook/web/lib/ai-planner/slice';
import { hideSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import { getSnackbarState } from '@bridebook/web/lib/bbcommon/selectors';
import { requestPushNotificationPermissions } from '@bridebook/web/lib/mobile-app/actions';
import { isCordovaPluginAvailable } from '@bridebook/web/lib/mobile-app/utils/cordova-plugin-check';
import { useCheckPushPermissions } from '@bridebook/web/lib/settings/hooks/use-check-push-permissions';
import { clickedAnalytics } from '@bridebook/web/lib/ui/analytics';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch, useSelector } from '@bridebook/web/lib/utils';

export type TSnackbarAction =
  | 'goToFavourites'
  | 'enableNotifications'
  | 'openAppSettings'
  | 'undoDisableAIPlanner';

const TOP_OFFSET = 56;

const SnackbarWrapper: React.FC = () => {
  const {
    show,
    mode,
    text,
    linesLimit,
    action,
    actionData,
    icon,
    uuid,
    timeout,
    analyticsData,
    customOffset,
    ctaContent,
  } = useSelector(getSnackbarState);
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const pushPermissions = useCheckPushPermissions();

  const handleEnableNotifications = useCallback(async () => {
    if (pushPermissions === 'prompt' || pushPermissions === 'prompt-with-rationale') {
      dispatch(requestPushNotificationPermissions());
    } else {
      if (isCordovaPluginAvailable('NativeSettings')) {
        NativeSettings.open({
          optionAndroid: AndroidSettings.AppNotification,
          optionIOS: IOSSettings.App,
        });
      }
    }
  }, [dispatch, pushPermissions]);

  const handleOpenAppSettings = useCallback(() => {
    if (isCordovaPluginAvailable('NativeSettings')) {
      NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App,
      });
    }
  }, []);

  const handleUndoDisableAIPlanner = useCallback(() => {
    dispatch(setAIPlannerDisabled(false));
    dispatch(hideSnackbar());
  }, [dispatch]);

  const handleCloseSnackbar = useCallback(() => {
    dispatch(clickedAnalytics({ ...analyticsData }));
    dispatch(hideSnackbar());
  }, [analyticsData, dispatch]);

  const availableSnackbarActions: Record<TSnackbarAction, ReactNode> = useMemo(() => {
    const href = actionData?.supplierType
      ? UrlHelper.shortlist.tabBySupplierType(actionData.supplierType).as
      : actionData?.supplierShortlistUrl;

    return {
      goToFavourites:
        actionData && href ? (
          <Link href={href} onClick={handleCloseSnackbar}>
            {t('snackbar.actions.goToFavourites.label')}
          </Link>
        ) : null,
      enableNotifications: (
        <ButtonV2 variant="link" size="small" onClick={handleEnableNotifications}>
          {t('snackbar.actions.enableNotifications.turnOn')}
        </ButtonV2>
      ),
      openAppSettings: (
        <ButtonV2 variant="link" size="small" onClick={handleOpenAppSettings}>
          {t('snackbar.actions.appSettings.goToSettings')}
        </ButtonV2>
      ),
      undoDisableAIPlanner: (
        <ButtonV2 variant="link" size="small" onClick={handleUndoDisableAIPlanner}>
          {t('snackbar.actions.undo.label')}
        </ButtonV2>
      ),
    };
  }, [
    actionData,
    handleCloseSnackbar,
    t,
    handleEnableNotifications,
    handleOpenAppSettings,
    handleUndoDisableAIPlanner,
  ]);

  const handleHideSnackbar = useCallback(() => {
    if (show) {
      dispatch(hideSnackbar());
    }
  }, [dispatch, show]);

  const getSnackbarCTA = useCallback((): ReactNode => {
    if (ctaContent?.href) {
      return (
        <Link href={ctaContent.href} onClick={handleCloseSnackbar}>
          {ctaContent.ctaLabel || t('common:snackbar.view.label')}
        </Link>
      );
    }

    return action ? availableSnackbarActions[action] : undefined;
  }, [action, availableSnackbarActions, ctaContent, handleCloseSnackbar, t]);

  return (
    <Snackbar
      show={show}
      uuid={uuid}
      timeout={timeout}
      onTimeout={handleHideSnackbar}
      mode={mode as TSnackbarMode}
      text={text}
      linesLimit={linesLimit}
      offset={customOffset || TOP_OFFSET}
      icon={icon}
      ctaContent={getSnackbarCTA()}
    />
  );
};

export default SnackbarWrapper;
